import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, NAME_TO_SLUG } from "@/lib/api";
import ScoutBox from "@/components/ScoutBox";

function scoreLabel(s) {
  if (s >= 80) return "Seasoned";
  if (s >= 60) return "Steady";
  if (s >= 40) return "Finding footing";
  return "Just voluntold";
}

export default function AssessmentResult() {
  const { accountId } = useParams();
  const { state } = useLocation();
  const [data, setData] = useState(state || null);

  useEffect(() => {
    if (!data) {
      api.get(`/assessment/result/${accountId}`).then(({ data }) => setData(data)).catch(() => {});
    }
    window.scrollTo(0, 0);
  }, [accountId]);

  if (!data) return <div className="max-w-2xl mx-auto px-4 py-24 text-[#6E6E6E]">Loading your dashboard…</div>;

  const cats = Object.entries(data.breakdown);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-12" data-testid="assessment-result-page">
      <span className="kicker">Your Coach Readiness Dashboard</span>

      <div className="mt-4 border-2 border-[#1E1E1E] bg-[#E7E3D9] p-8 flex flex-col sm:flex-row items-center gap-8 rise">
        <div className="text-center shrink-0">
          <div className="font-head font-semibold text-7xl text-[#1E1E1E] leading-none" data-testid="overall-score">{data.overall}</div>
          <div className="kicker mt-1">out of 100</div>
        </div>
        <div className="border-l-0 sm:border-l-2 border-[#D3CFC0] sm:pl-8">
          <div className="font-head uppercase text-2xl text-[#2D2D2D]">{scoreLabel(data.overall)}</div>
          <p className="mt-2 text-[#6E6E6E] text-[15px] leading-relaxed">
            Your growth edge right now is <strong className="text-[#1E1E1E]">{data.weak_category}</strong>. That's where Scout is pointing you first.
          </p>
        </div>
      </div>

      <h2 className="mt-12 font-head font-semibold uppercase text-2xl text-[#1E1E1E] border-b-2 border-[#D3CFC0] pb-2">The breakdown</h2>
      <div className="mt-6 space-y-5" data-testid="breakdown">
        {cats.map(([name, val]) => (
          <div key={name}>
            <div className="flex justify-between items-baseline mb-1">
              <span className={`font-head uppercase tracking-wide text-sm ${name === data.weak_category ? "text-[#E8A33D]" : "text-[#1E1E1E]"}`}>{name}</span>
              <span className="font-head text-lg text-[#1E1E1E]">{val}</span>
            </div>
            <div className="h-2 bg-[#D3CFC0]">
              <div className={`h-full transition-all duration-700 ${name === data.weak_category ? "bg-[#E8A33D]" : "bg-[#2D2D2D]"}`} style={{ width: `${val}%` }} />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12">
        <ScoutBox title="Scout's recommended next step" testId="scout-next-step">
          <p>{data.scout_recommendation}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to={data.recommended_tool.path} data-testid="reco-tool-link"
              className="inline-flex items-center gap-2 bg-[#2D2D2D] text-[#F3F1EC] font-head uppercase tracking-wide text-xs px-4 py-2 hover:bg-[#242424] transition-colors">
              {data.recommended_tool.name} <ArrowRight size={13} />
            </Link>
            <Link to="/products" data-testid="reco-product-link"
              className="inline-flex items-center gap-2 border-2 border-[#2D2D2D] text-[#2D2D2D] font-head uppercase tracking-wide text-xs px-4 py-2 hover:bg-[#2D2D2D] hover:text-[#F3F1EC] transition-colors">
              {data.recommended_product.name.split("–")[0].trim()} · ${data.recommended_product.amount / 100}
            </Link>
          </div>
        </ScoutBox>
      </div>

      <div className="mt-8 border border-[#D3CFC0] p-5">
        <p className="text-[#6E6E6E] text-sm">
          Scout also picked a read for you in <strong className="text-[#1E1E1E]">{data.recommended_article_category}</strong>.
        </p>
        <Link to={`/category/${NAME_TO_SLUG[data.recommended_article_category]}`} className="mt-2 inline-flex items-center gap-1 text-[#2D2D2D] link-line text-sm">
          Open {data.recommended_article_category} <ArrowRight size={14} />
        </Link>
      </div>

      <p className="mt-8 text-xs text-[#6E6E6E]">A copy of this — Scout's take, framed for your inbox — is on its way to your email.</p>
    </div>
  );
}
